// Journey Lab's one presentation-language owner. Execution records keep their exact domain enums; this
// module translates those enums into the approved plain-language labels and proof boundaries. Unknown or
// incomplete mode/evidence never receives a successful-looking fallback.

import type {
  JourneyExecutionMode,
  JourneyLiveExecutionMode,
  JourneyRunView,
  JourneyVerdict,
} from './journey-lab-api.ts';

export interface JourneyModePresentation {
  readonly key: 'scripted' | 'no_egress_rehearsal' | 'provider_live' | 'unrecorded';
  readonly marker: 'scripted' | 'no-egress-rehearsal' | 'provider-live' | 'unrecorded';
  readonly name: string;
  readonly label: string;
  /** One sentence stating exactly what the recorded mode does and does not prove. */
  readonly proofBoundary: string;
  readonly externalServices: 'not_used' | 'permitted' | 'used' | 'unproven';
}

const SCRIPTED: JourneyModePresentation = {
  key: 'scripted',
  marker: 'scripted',
  name: 'Scripted check',
  label: 'Scripted check — simulated services',
  proofBoundary:
    'This proves the deterministic Ritona domain rails only; the model, browser, internet email, CRM and other external providers were not used.',
  externalServices: 'not_used',
};

const NO_EGRESS: JourneyModePresentation = {
  key: 'no_egress_rehearsal',
  marker: 'no-egress-rehearsal',
  name: 'Full-flow rehearsal',
  label: 'Full-flow rehearsal — no external actions',
  proofBoundary:
    'This proves the full orchestration and durable evidence lifecycle without external actions; the browser, model runtime, internet email, CRM and Ritona product data were not used.',
  externalServices: 'not_used',
};

const PROVIDER_BACKED: JourneyModePresentation = {
  key: 'provider_live',
  marker: 'provider-live',
  name: 'Provider-backed test',
  label: 'Provider-backed test — external access permitted',
  proofBoundary:
    'This mode permits the reviewed provider-backed test bindings; actual external use is claimed only where independently observed provider evidence is recorded.',
  externalServices: 'permitted',
};

const PROVIDER_USED: JourneyModePresentation = {
  ...PROVIDER_BACKED,
  label: 'Provider-backed test — external services used',
  proofBoundary:
    'This run used the reviewed provider-backed test bindings and recorded provider-corroborated model evidence; every other provider result still depends on its own retained evidence.',
  externalServices: 'used',
};

const PROVIDER_UNPROVEN: JourneyModePresentation = {
  ...PROVIDER_BACKED,
  label: 'Provider-backed test — external use not proven',
  proofBoundary:
    'This run was bound to the reviewed provider-backed test configuration, but independently observed provider use is missing or not corroborated.',
  externalServices: 'unproven',
};

const UNRECORDED: JourneyModePresentation = {
  key: 'unrecorded',
  marker: 'unrecorded',
  name: 'Execution mode not recorded',
  label: 'Execution mode not recorded',
  proofBoundary:
    'The evidence does not identify an exact execution mode, so no simulated, no-external-action or provider-backed result is claimed.',
  externalServices: 'unproven',
};

/** The exact recorded-run mode presentation. Missing or unknown live detail fails closed to unrecorded. */
export function journeyModePresentation(
  mode: JourneyExecutionMode,
  liveMode?: JourneyLiveExecutionMode,
): JourneyModePresentation {
  if (mode === 'scripted') return SCRIPTED;
  if (mode !== 'live') return UNRECORDED;
  if (liveMode === 'no_egress_rehearsal') return NO_EGRESS;
  if (liveMode === 'provider_live') return PROVIDER_BACKED;
  return UNRECORDED;
}

/** The preflight/arm mode is already exact; an invalid runtime value still fails closed. */
export function journeyLiveModePresentation(
  mode: JourneyLiveExecutionMode,
): JourneyModePresentation {
  if (mode === 'no_egress_rehearsal') return NO_EGRESS;
  if (mode === 'provider_live') return PROVIDER_BACKED;
  return UNRECORDED;
}

/** A recorded provider-backed Run claims use only after independently observed model evidence corroborates it. */
export function journeyRunModePresentation(
  run: Pick<JourneyRunView, 'executionMode' | 'liveMode' | 'modelVerification'>,
): JourneyModePresentation {
  const mode = journeyModePresentation(run.executionMode, run.liveMode);
  if (mode.key !== 'provider_live') return mode;
  return run.modelVerification === 'verified' ? PROVIDER_USED : PROVIDER_UNPROVEN;
}

/** Catalogue capability is not run evidence. Expand `live` into the two exact approved choices. */
export function journeyAvailableModeLabels(mode: JourneyExecutionMode): readonly string[] {
  if (mode === 'scripted') return [SCRIPTED.label];
  if (mode === 'live') return [NO_EGRESS.label, PROVIDER_BACKED.label];
  return [UNRECORDED.label];
}

export const JOURNEY_AVAILABILITY_PROOF_BOUNDARY =
  'Available checks describe what can be started; only a recorded run proves which services were used.';

/** Exact approved verdict language. Missing or invalid evidence is never presented as a neutral finish. */
export function journeyVerdictLabel(verdict: JourneyVerdict | undefined): string {
  if (verdict === 'PASS') return 'Passed';
  if (verdict === 'FAIL') return 'Failed';
  if (verdict === 'BLOCKED') return 'Blocked before completion';
  return 'Required evidence missing';
}
